import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ReportSection } from '@/components/reports/ReportSection'
import { ReportStates, bucketLabel, downloadCsv, tooltipStyle } from './reportShared'
import { useProcedureThroughputReport } from '@/hooks/useProcedureVolumeReport'
import { reportFileName, toCsv } from '@/lib/reportCsv'
import type { ReportFilterSelection } from '@/lib/reportFilters'
import { windowLabel, type ReportTimeFrame } from '@/lib/reportRange'

/**
 * Report 1.2 — procedures received vs completed.
 *
 * The question is whether the queue is growing or draining, so the two series
 * are shown together and their difference is given its own row: intake against
 * output, then the net.
 *
 * A run received in one bucket and completed in another appears in both, in
 * different buckets. That is not double counting — the gap between the series
 * IS the backlog changing, which is the whole report.
 *
 * Two colours only, and they are the first two categorical slots. Received is
 * the neutral blue and completed the warm one, so "how much came in" reads as
 * the baseline and "how much went out" as the thing measured against it.
 */
const RECEIVED = '#2a78d6'
const COMPLETED = '#1baf7a'
const NET = '#eb6834'

export function ProcedureThroughputReport({
  frame,
  filters,
}: {
  frame: ReportTimeFrame
  filters: ReportFilterSelection
}) {
  const query = useProcedureThroughputReport(frame, filters)
  const state = ReportStates(query)
  if (state) return state
  // Narrowing for the compiler: ReportStates has already returned for every
  // state in which `data` is absent.
  const report = query.data
  if (!report) return null
  const data = report.buckets.map((b) => ({ ...b, label: bucketLabel(b.bucket, report.grain) }))

  const exportCsv = () => {
    downloadCsv(
      toCsv(
        ['Bucket', 'Received', 'Completed', 'Net'],
        report.buckets.map((b) => [b.bucket, b.received, b.completed, b.net]),
      ),
      reportFileName('procedures-received-vs-completed', report.window, report.grain),
    )
  }

  return (
    <div className='flex flex-col gap-4 p-4'>
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
        <Figure label='Received' value={report.totalReceived} tone='text-foreground' />
        <Figure label='Completed' value={report.totalCompleted} tone='text-green-600' />
        <Figure
          label={report.net > 0 ? 'Backlog grew by' : report.net < 0 ? 'Backlog shrank by' : 'Backlog unchanged'}
          value={Math.abs(report.net)}
          tone={report.net > 0 ? 'text-orange-500' : 'text-green-600'}
        />
      </div>

      <ReportSection
        title='Received vs completed'
        subtitle={`${windowLabel({ from: report.window.from, to: report.window.to })} · ${report.grain}`}
        onExport={report.buckets.length > 0 ? exportCsv : undefined}
      >
        <div className='h-72 w-full px-2 py-4'>
          <ResponsiveContainer width='100%' height='100%'>
            <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#e5e7eb' />
              <XAxis
                dataKey='label'
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval='preserveStartEnd'
                minTickGap={16}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickLine={false}
                axisLine={false}
                width={32}
              />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={tooltipStyle} />
              <Legend iconType='square' wrapperStyle={{ fontSize: 12 }} />
              {/* Bars for the two counts, a line for their difference. One axis:
                  all three are counts of procedures, so they share a scale. */}
              <Bar dataKey='received' name='Received' fill={RECEIVED} radius={[4, 4, 0, 0]} maxBarSize={28} />
              <Bar dataKey='completed' name='Completed' fill={COMPLETED} radius={[4, 4, 0, 0]} maxBarSize={28} />
              <Line
                type='monotone'
                dataKey='net'
                name='Net change'
                stroke={NET}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </ReportSection>

      <ReportSection title='By bucket'>
        <div className='max-h-80 overflow-x-persistent'>
          <table className='w-full text-sm'>
            <thead className='sticky top-0 bg-muted/40'>
              <tr className='border-b text-left text-xs uppercase tracking-wide text-muted-foreground'>
                <th className='px-4 py-2 font-medium'>Bucket</th>
                <th className='px-4 py-2 text-right font-medium'>Received</th>
                <th className='px-4 py-2 text-right font-medium'>Completed</th>
                <th className='px-4 py-2 text-right font-medium'>Net</th>
              </tr>
            </thead>
            <tbody>
              {report.buckets.map((b) => (
                <tr key={b.bucket} className='border-b last:border-0'>
                  <td className='px-4 py-1.5'>{bucketLabel(b.bucket, report.grain)}</td>
                  <td className='px-4 py-1.5 text-right tabular-nums'>{b.received}</td>
                  <td className='px-4 py-1.5 text-right tabular-nums'>{b.completed}</td>
                  <td
                    className={`px-4 py-1.5 text-right tabular-nums ${
                      b.net > 0 ? 'text-orange-600' : b.net < 0 ? 'text-green-600' : ''
                    }`}
                  >
                    {b.net > 0 ? `+${b.net}` : b.net}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {report.completedUndated > 0 && (
          <p className='border-t px-4 py-2 text-xs text-muted-foreground'>
            {report.completedUndated} with no completion time, in neither series.
          </p>
        )}
      </ReportSection>
    </div>
  )
}

function Figure({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className='rounded-xl border bg-background p-3 shadow-sm'>
      <p className='text-xs text-muted-foreground'>{label}</p>
      <p className={`mt-0.5 text-2xl font-semibold tabular-nums ${tone}`}>{value}</p>
    </div>
  )
}
